import defaultBannerImage from 'static/images/default-page-banner.jpg';
import Error from './error/error-page';
import getCanonicalUrl from 'app/utilities/get-canonical-url';
import { getOpenGraphSlug } from 'app/utilities/url';
import Helmet from 'react-helmet';
import { isAppProd } from 'config/env';
import JsonLd from 'app/utilities/JsonLd';
import NotFound from './error/not-found-page';
import printBreadcrumbList from 'app/utilities/print-breadcrumb-list';
import PropTypes from 'prop-types';
import React from 'react';
import { RESPONSE } from 'config/api';
import Template from 'app/components/templates/index';
import { useLocation } from 'react-router-dom';
import { generateArticleSchema, generateFAQPageSchema, generatePageSchema, seoDomain } from 'config/seo';
import {
    getCategoryFilters,
    getListingLayout,
    getNewsTemplateType,
    getPageNumber,
    isNewsListingTemplate,
    LAYOUT,
    resolveCategories
} from 'app/utilities/news-listing';
import normalizeNewsPageTitle, { appendPageNumber } from 'app/utilities/normalize-news-page-title';

const ContentPage = ({ featuredNews, globalFields, latestNews, listingItems, newsCategories, newsSubCategories, page, popularNews }) => {
    const { search } = useLocation();
    const { new_ticket_flow_slug: newTicketFlowSlug } = globalFields;

    // Render not error/not found page
    if (!page || page.error && page.error.status === RESPONSE.NOT_FOUND) return <NotFound />;
    if (page.error && page.error.status === RESPONSE.INTERNAL_SERVER_ERROR || !page.attributes) return <Error />;

    const { id, type, attributes } = page;

    const {
        author: author,
        is_published: isPublished,
        level,
        name: title,
        parent,
        short_description: shortDescription,
        thumbnail,
        seo_description: seoDescription,
        seo_keywords: seoKeywords,
        seo_title: seoTitle,
        slug
    } = attributes;

    // A collection the CMS has nothing in comes back as null rather than being
    // left out of the response, and a default only stands in for a field that is
    // missing altogether
    const blocks = attributes.blocks || [];
    const mainImages = attributes.main_images || [];

    const pageTitle = seoTitle ? seoTitle : title;
    const isNewsEntity = type === 'news' || type === 'news-category' || type === 'news-categories';
    const normalisedTitle = isNewsEntity ? normalizeNewsPageTitle(pageTitle) : pageTitle;

    const openGraphSlug = getOpenGraphSlug(slug, parent, type);
    const schemaDescription = seoDescription || shortDescription || title;
    let image = mainImages[0] || thumbnail;

    if (!image) {
        image = `https://www.aucklandzoo.co.nz${defaultBannerImage}`;
    }

    let templateTypeToUse; // eslint-disable-line one-var
    if (slug === newTicketFlowSlug) {
        templateTypeToUse = 'tickets';
    } else if (slug === 'day-pass') {
        templateTypeToUse = 'day-pass';
    } else {
        templateTypeToUse = getNewsTemplateType(type, slug, level, parent);
    }

    const isListing = isNewsListingTemplate(templateTypeToUse);
    // The `/news` landing page filters by level 1 category, a category page by
    // its own children
    const listingCategories = templateTypeToUse === 'news' ? newsCategories : newsSubCategories;
    // Unknown slugs are dropped and the rest are put in collection order, so a
    // mistyped or reordered query cannot produce a canonical of its own
    const activeCategories = isListing ?
        resolveCategories(listingCategories, getCategoryFilters(search)).map((category) => category.slug) :
        [];
    const listingPage = isListing ? getPageNumber(search) : 1;

    // Only the pages that stand on their own are told apart by their titles. A
    // filtered listing canonicalises elsewhere, and so does every page of the
    // category grid, whose pages hold each other's articles rather than their own.
    const hasOwnTitle = isListing && !activeCategories.length && getListingLayout(templateTypeToUse) !== LAYOUT.GRID;
    const titleToUse = hasOwnTitle ? appendPageNumber(normalisedTitle, listingPage) : normalisedTitle;
    const canonicalUrl = getCanonicalUrl(slug, templateTypeToUse, parent, { activeCategories, page: listingPage });
    const pageUrl = canonicalUrl || `${seoDomain}/${openGraphSlug.replace(/^\/+/, '')}`;

    /*
        A page number past the end of the listing renders nothing. It is not linked
        from anywhere, because the arrow stops offering a next page at the last one,
        but it answers 200 and points at itself, which is an empty page offered up
        for indexing if it is ever linked from outside.

        Read from what was rendered rather than from the page number, so it holds
        however many articles the listing turns out to have. Only an array counts:
        articles yet to arrive are undefined, and are not an empty listing.
    */
    const isEmptyListingPage = isListing &&
        listingPage > 1 &&
        Array.isArray(listingItems) &&
        !listingItems.length;

    const isNoIndex = !isAppProd || !isPublished || isEmptyListingPage;
    const pageSchema = generatePageSchema({
        url: pageUrl,
        name: titleToUse,
        description: schemaDescription,
        image
    });
    const faqSchema = generateFAQPageSchema({
        url: pageUrl,
        name: titleToUse,
        description: schemaDescription,
        blocks
    });
    const articleSchema = templateTypeToUse === 'article' ?
        generateArticleSchema({ ...attributes, type }, pageUrl) :
        null;

    return (
        <main role="main" className="main">
            <Helmet>
                {isNoIndex && <meta name="robots" content="noindex, nofollow" />}

                <title>{titleToUse}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                {author && <meta name="author" content={author} /> }

                {/* Schema.org for Google+ */}
                <meta itemProp="name" content={titleToUse} />
                <meta itemProp="description" content={seoDescription} />
                <meta itemProp="image" content={image} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={titleToUse} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={image} />

                {/* Open Graph */}
                {/* <meta property="fb:app_id" content={FACEBOOK_APP_ID} /> */}
                {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                <meta property="og:locale" content="en_NZ" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={titleToUse} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:image" content={image} />
            </Helmet>
            <Template
                type={templateTypeToUse}
                activeCategories={activeCategories}
                data={attributes}
                featuredNews={featuredNews}
                globalFields={globalFields}
                id={id}
                latestNews={latestNews}
                listingPage={listingPage}
                newsCategories={newsCategories}
                newsSubCategories={newsSubCategories}
                popularNews={popularNews}
            />
            <JsonLd item={pageSchema} />
            {faqSchema && <JsonLd item={faqSchema} />}
            {articleSchema && <JsonLd item={articleSchema} />}
            {printBreadcrumbList(page)}
        </main>
    );
};

ContentPage.propTypes = {
    featuredNews: PropTypes.array,
    globalFields: PropTypes.object.isRequired,
    latestNews: PropTypes.array,
    listingItems: PropTypes.array,
    newsCategories: PropTypes.array,
    newsSubCategories: PropTypes.array,
    page: PropTypes.object.isRequired,
    popularNews: PropTypes.array
};

export default ContentPage;
